import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { computed, makeObservable } from 'mobx'
import Job from 'jobs/Job'
import { timeago } from 'utils/date-util'
import SearchableJob from '../../../../SearchableJob'
import { JobTicket } from '../../../../tickets/JobTicket'
import { JobWorkspaceFilter } from 'jobs/infra/JobSearchViewModel'

/**
 * Getter for the currently active job.
 */
export type GetActiveJob = () => Job | null | undefined

/**
 * Getter for the workspace filter being used.
 */
export type GetWorkspaceFilter = () => JobWorkspaceFilter

/**
 * View model for a job item in the list.
 */
export class JobItemViewModel {
  /**
   * Constructor.
   */
  constructor(
    public job: SearchableJob,
    private getActiveJob: GetActiveJob,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private getLatestOpenTicket: () => JobTicket | null,
  ) {
    makeObservable(this)
  }

  /**
   * Whether we need to render workspace information.
   */
  @computed
  get isWorkspaceInfoRelevant() {
    // Only show the workspace information if the user is a member
    // of more than one workspace.
    return this.getCurrentUserWorkspaceMemberships().length > 1
  }

  /**
   * Collaborators on the job that are not you.
   */
  @computed
  get collaborators() {
    return this.job.members.filter((m) => !m.isCurrentUser)
  }

  /**
   * Gets the unread count.
   */
  @computed
  get unreadCount() {
    return this.job.conversation ? this.job.conversation.unreadCount : 0
  }

  /**
   * The last activity timestamp.
   */
  @computed
  get lastActivity() {
    return timeago(this.job.dateModified!)
  }

  /**
   * Whether this is the currently displayed job.
   */
  @computed
  get isActive() {
    const activeJobId = this.getActiveJob()?.id

    return activeJobId === this.job.id
  }

  /**
   * Returns the job name. If the job doesn't have a custom name,
   * it returns the computed title from the legend, else the legend name
   */
  @computed
  get jobName() {
    return this.job.name || this.job.description || this.job.legendName
  }

  /**
   * Returns the latest open ticket for the job.
   */
  @computed
  get latestOpenTicket() {
    return this.getLatestOpenTicket()
  }

  /**
   * Called when the view model becomes relevant.
   */
  activate() {
    //
  }

  /**
   * Called when the view model is no longer actively used.
   */
  deactivate() {
    //
  }
}
